import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getInterviewByPath, chroInterviews } from "./chroInterviewsData";
import "../Styles/chroInterviews.css";
import "../Styles/chroInterviewDetail.css";

/*
  CHRO INTERVIEWS: list page + detail page in one file.
    DATA is in chroInterviewsData.js (add new interviews at the TOP there).
    2. DETAIL  -> one interview page (questions, TOC, share, certificate)
    3. LIST    -> the cards page with "Read More"
    4. ENTRY   -> shows the detail page when the URL matches an interview,
                  otherwise shows the list
*/

// ===================================================================
// 2. DETAIL PAGE
// ===================================================================
const CALENDAR_ICON = "/chro_icons_calendar.png";
const ARROW_ICON = "/chro-_arrow-down.png";
// Certificate banner artwork. Export it WITHOUT the "Get Certificate" button,
// the real button is rendered on top of it below.
const CERT_BANNER = "/chro_cert_banner.png";

const qid = (n) => `chrodtl-q-${n}`;

// Table of contents: closed on phones (< 768px), open on tablet / laptop / desktop
const TOC_OPEN_FROM = 768;

// "**Name** is ..." -> <strong>Name</strong> is ...
const boldify = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

// An answer can be a string, or an array of paragraphs / { list: [...] } blocks.
const renderAnswer = (a) => {
  const blocks = Array.isArray(a) ? a : [a];
  return blocks.map((b, i) =>
    typeof b === "string" ? (
      <p key={i}>{b}</p>
    ) : (
      <ul key={i} className="chrodtl-list">
        {b.list.map((li, j) => {
          const k = li.indexOf(":");
          const hasLabel = k > 0 && k < 45;
          return (
            <li key={j}>
              {hasLabel ? <strong>{li.slice(0, k + 1)}</strong> : null}
              {hasLabel ? li.slice(k + 1) : li}
            </li>
          );
        })}
      </ul>
    )
  );
};

const ChroInterviewDetail = ({ interview }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [tocOpen, setTocOpen] = useState(
    typeof window === "undefined" ? true : window.innerWidth >= TOC_OPEN_FROM
  );
  const [active, setActive] = useState(1);
  const listRef = useRef(null);
  const lockUntil = useRef(0); // ignore scroll-spy briefly after a TOC click
  const syncActive = useRef(() => {}); // re-computes the highlighted question from the scroll position
  const tocRef = useRef(null);    // the whole contents box (for "tap outside to close")
  const layoutRef = useRef(null); // the contents + answers area (floating bar only shows while it is on screen)
  const [floatShown, setFloatShown] = useState(true);

  // reset when another interview is opened
  useEffect(() => {
    setActive(1);
    setTocOpen(window.innerWidth >= TOC_OPEN_FROM); // phones: closed first, always
    window.scrollTo(0, 0);
    document.title = `${interview.heroTitle} ${interview.heroAccent || ""}`.trim();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // PHONES: the contents box floats at the bottom of the screen.
  // It is only shown while the questions are on screen, so it never covers the footer.
  useEffect(() => {
    const layout = layoutRef.current;
    if (!layout || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => {
      setFloatShown(entry.isIntersecting);
      if (!entry.isIntersecting && window.innerWidth < TOC_OPEN_FROM) setTocOpen(false);
    });
    io.observe(layout);
    return () => io.disconnect();
  }, [interview]);

  // PHONES: while the floating list is open, keep the highlight on the question being read
  useEffect(() => {
    if (!tocOpen || window.innerWidth >= TOC_OPEN_FROM) return;
    syncActive.current();
    const id = window.setInterval(() => syncActive.current(), 250);
    return () => window.clearInterval(id);
  }, [tocOpen]);

  // PHONES: tapping outside the open list, or pressing Esc, closes it
  useEffect(() => {
    if (!tocOpen || window.innerWidth >= TOC_OPEN_FROM) return;
    const onDown = (e) => {
      if (tocRef.current && !tocRef.current.contains(e.target)) setTocOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setTocOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [tocOpen]);

  // crossing the phone / tablet breakpoint (rotate, resize) re-applies the open/closed rule
  useEffect(() => {
    let wide = window.innerWidth >= TOC_OPEN_FROM;
    const onResize = () => {
      const nowWide = window.innerWidth >= TOC_OPEN_FROM;
      if (nowWide !== wide) {
        wide = nowWide;
        setTocOpen(nowWide);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // highlight the TOC item for the question being read.
  // "Reading line" = 140px from the top of the screen (just under the sticky header):
  // the last question whose top has passed that line is the active one.
  useEffect(() => {
    const total = interview.qa.length;
    if (!total) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      if (Date.now() < lockUntil.current) return; // a TOC click is still scrolling
      const atBottom =
        window.scrollY > 0 &&
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atBottom) {
        setActive(total);
        return;
      }
      let current = 1;
      for (let n = 1; n <= total; n++) {
        const el = document.getElementById(qid(n));
        if (el && el.getBoundingClientRect().top <= 140) current = n;
        else break;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    syncActive.current = update;
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [interview]);

  // keep the active TOC row visible inside the TOC scroll area (never scrolls the page)
  useEffect(() => {
    const list = listRef.current;
    const el = list && list.querySelector(".chrodtl-toc-item.active");
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop || bottom > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: top - list.clientHeight / 2, behavior: "smooth" });
    }
  }, [active, tocOpen]);

  const goTo = (n) => {
    const el = document.getElementById(qid(n));
    lockUntil.current = Date.now() + 900;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(n);
    if (window.innerWidth < TOC_OPEN_FROM) setTocOpen(false); // phones: reveal the answer
  };

  // "Section 1", "Section 2"... counted in the order the groups appear in the data
  let sectionCount = 0;
  const sectionNo = interview.qa.map((item) => (item.section ? ++sectionCount : 0));

  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(`${interview.heroTitle} ${interview.heroAccent || ""}`.trim());
  const shares = [
    { label: "WhatsApp", href: `https://wa.me/?text=${text}%20${url}`, icon: "/chro-WhatsApp.png" },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, icon: "/chro-LinkedIn-Circled.png" },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${url}`, icon: "/chro-Facebook.png" },
    { label: "Twitter", href: `https://twitter.com/intent/tweet?url=${url}&text=${text}`, icon: "/chro-Twitter-Bird.png" },
  ];

  return (
    <div className="chrodtl-page">
      {/* HERO */}
      <section className="chrodtl-hero">
        <div className="chrodtl-hero-inner">
          <div className="chrodtl-hero-text">
            <h1 className="chrodtl-title">
              {interview.heroTitle}
              {interview.heroAccent && (
                <>
                  {" "}
                  <span className="chrodtl-title-accent">{interview.heroAccent}</span>
                </>
              )}
            </h1>
            <div className="chrodtl-date">
              <img src={CALENDAR_ICON} alt="" className="chrodtl-date-icon" />
              <span>{interview.date}</span>
            </div>
          </div>

          <div className="chrodtl-hero-media">
            <span className="chrodtl-deco-square" aria-hidden="true" />
            <img src={interview.heroImage} alt={interview.heroAlt} className="chrodtl-hero-img" />
            <svg className="chrodtl-deco-rings" viewBox="0 0 100 100" aria-hidden="true">
              {[48, 37, 26, 15].map((r) => (
                <circle key={r} cx="50" cy="50" r={r} fill="none" stroke="#0B1D51" strokeWidth="1.2" />
              ))}
            </svg>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="chrodtl-body">
        <div className="chrodtl-layout" ref={layoutRef}>
          {/* TABLE OF CONTENTS */}
          <aside
            className={`chrodtl-toc${floatShown ? "" : " chrodtl-toc--hidden"}`}
            aria-label="Table of contents"
            ref={tocRef}
          >
            <button
              type="button"
              className="chrodtl-toc-head"
              onClick={() => setTocOpen((o) => !o)}
              aria-expanded={tocOpen}
            >
              Table of Contents
              <img src={ARROW_ICON} alt="" className={`chrodtl-chevron${tocOpen ? " open" : ""}`} />
            </button>

            {tocOpen && (
              <ol className="chrodtl-toc-list" ref={listRef}>
                {interview.qa.map((item, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className={`chrodtl-toc-item${active === i + 1 ? " active" : ""}`}
                      onClick={() => goTo(i + 1)}
                      title={`Q${i + 1}: ${item.q}`}
                    >
                      Q{i + 1}: {item.q}
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </aside>

          {/* QUESTIONS & ANSWERS */}
          <article className="chrodtl-content">
            {interview.intro && interview.intro.length > 0 && (
              <div className="chrodtl-intro">
                {interview.intro.map((t, i) => (
                  <p key={i}>{boldify(t)}</p>
                ))}
              </div>
            )}
            {interview.qa.length === 0 && (
              <p className="chrodtl-empty">The full interview will be published here soon.</p>
            )}
            {interview.qa.map((item, i) => (
              <div className="chrodtl-qa" id={qid(i + 1)} data-index={i + 1} key={i}>
                {item.section && (
                  <div className={`chrodtl-section${i > 0 ? " chrodtl-section--rule" : ""}`}>
                    <h2>
                      Section {sectionNo[i]}: {item.section}
                    </h2>
                  </div>
                )}
                <h3>Q{i + 1}: {item.q}</h3>
                {renderAnswer(item.a)}
              </div>
            ))}
          </article>

          {/* SHARE + CERTIFICATE */}
          <aside className="chrodtl-side">
            <div className="chrodtl-share">
              <span className="chrodtl-share-label">Share :</span>
              {shares.map(({ label, href, icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Share on ${label}`}>
                  <img src={icon} alt="" />
                </a>
              ))}
            </div>

            <div
              className="chrodtl-cert"
              style={{ backgroundImage: `url(${CERT_BANNER})` }}
              role="img"
              aria-label="Earn the Outstanding Workplaces Certification"
            >
              <button type="button" className="chrodtl-cert-btn" onClick={() => {
                  navigate("/nominate");
                  window.scrollTo(0, 0);
                }}>
                Get Certificate
              </button>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

// ===================================================================
// 3. LIST PAGE
// ===================================================================
// Single hero image on the right 👉 /public/chro_hero.png
const HERO_IMAGE = "/chro_hero.png";

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChroInterviewList = () => {
  const navigate = useNavigate();

  const open = (path) => {
    if (!path) return;
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="chro-page">
      {/* HERO */}
      <section className="chro-hero">
        <div className="chro-hero-inner">
          <div className="chro-hero-text">
            <span className="chro-eyebrow">CHRO Interviews</span>
            <h1 className="chro-title">
              The Leaders
              <span className="chro-title-accent">Who Shape Culture</span>
            </h1>
            <p className="chro-desc">
              Conversations with CHROs and people leaders on leadership, employee experience,
              and the power of building workplaces where people thrive.
            </p>
          </div>

          <div className="chro-hero-media">
            <span className="chro-deco-square" aria-hidden="true" />
            <img src={HERO_IMAGE} alt="CHRO interview discussion" className="chro-hero-img" />
            <svg className="chro-deco-rings" viewBox="0 0 100 100" aria-hidden="true">
              {[48, 37, 26, 15].map((r) => (
                <circle key={r} cx="50" cy="50" r={r} fill="none" stroke="#0B1D51" strokeWidth="1.2" />
              ))}
            </svg>
          </div>
        </div>
      </section>

      {/* CARDS */}
      <section className="chro-list">
        <div className="chro-grid">
          {chroInterviews.map((item) => (
            <article className="chro-card" key={item.id}>
              <Link
                to={item.path}
                className="chro-card-image"
                onClick={() => window.scrollTo(0, 0)}
                aria-label={`Read: ${item.listTitle}`}
              >
                <img src={item.listImage} alt="" />
              </Link>
              <div className="chro-card-body">
                <h3 className="chro-card-title">{item.listTitle}</h3>
                <div className="chro-card-date">
                  <img src={CALENDAR_ICON} alt="" className="chro-card-date-icon" />
                  <span>{item.date}</span>
                </div>
                <button type="button" className="chro-readmore" onClick={() => open(item.path)}>
                  Read More <ArrowIcon />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

// ===================================================================
// 4. ENTRY: this is what App.js renders for every CHRO route
// ===================================================================
const ChroInterviews = () => {
  const { pathname } = useLocation();
  const interview = getInterviewByPath(pathname);
  return interview ? <ChroInterviewDetail interview={interview} /> : <ChroInterviewList />;
};

export default ChroInterviews;